import React, { PureComponent } from 'react';
import moment from 'moment-timezone';
import { UserWorkoutSession } from 'fitbeat.gym-core';
import { BookingState } from 'fitbeat.models';
import { StyleSheet, View, ViewStyle } from 'react-native';
import { Button, ButtonTypes, ButtonSize } from '../../components';
import { GymResolver } from '../../services';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { isWeb } from '../../utils/ScreenSizes';
import { getOpenInviteLink } from '../../utils/inviteHelper';
import TrashIcon from '../../assets/icons/trash-3.svg';
import { TranslateProp } from '../../common-types';

interface IProps {
  t: TranslateProp;
  gymTimezone: string;
  changeBooking: () => void;
  cancelBooking: () => void;
  workoutSession: UserWorkoutSession;
  sendInvite: () => void;
  changeInvite?: () => void;
  shareOpenInvite?: () => void;
  closeModal?: () => void;
  cancelInvite?: () => void;
  renderCancelOnly?: boolean;
  canCheckIn?: boolean;
  checkInPressed?: () => void;
  checkingInLoading?: boolean;
  canStillEditClass?: boolean;
}

interface IState {
  copyLinkMessage: string;
}

class BookingOperationButtons extends PureComponent<IProps, IState> {
  public state: IState = {
    copyLinkMessage: this.props.t('copyInvite'),
  };

  public renderSendInviteButton = () => {
    const { t, sendInvite } = this.props;
    return (
      <Button
        title={t('sendInviteButton')}
        onPress={sendInvite}
        fontColor={Theme.WHITE}
        size={ButtonSize.MEDIUM}
      />
    );
  };

  public renderChangeInvite = () => {
    const { t, changeInvite } = this.props;
    return (
      <Button
        title={t('changeInvite')}
        onPress={changeInvite}
        fontColor={Theme.WHITE}
        size={ButtonSize.MEDIUM}
        backgroundColor={Theme.PRIMARY}
      />
    );
  };

  public renderCancelBookingButton = (buttonStyle: ViewStyle = {}) => {
    const { t, cancelBooking } = this.props;
    const iconSize = defaultScale(27);

    const trashIcon = (
      <TrashIcon
        width={iconSize}
        height={iconSize}
        style={{
          marginRight: defaultScale(4),
          alignSelf: 'center',
          color: Theme.PRIMARY,
        }}
        viewBox='0 0 24 24'
      />
    );

    return (
      <Button
        fontColor={Theme.PRIMARY}
        size={ButtonSize.MEDIUM}
        buttonType={ButtonTypes.OUTLINE}
        title={t('delete')}
        onPress={cancelBooking}
        containerStyle={buttonStyle}
        icon={trashIcon}
      />
    );
  };

  public renderCancelInviteButton = () => {
    const { t, cancelInvite } = this.props;
    return (
      <Button
        title={t('cancelInviteButton')}
        onPress={cancelInvite}
        fontColor={Theme.WHITE}
      />
    );
  };

  public renderShareOpenInviteButton = () => {
    const { t, shareOpenInvite, workoutSession } = this.props;

    const shareInvite = async () => {
      if (isWeb()) {
        const inviteLink = await getOpenInviteLink(workoutSession);
        navigator.clipboard.writeText(inviteLink)
          .then(() => {
            this.setState({ copyLinkMessage: t('copiedInvite') });
          }).catch(() => {
            this.setState({ copyLinkMessage: t('copyInvite') });
          });
      } else {
        shareOpenInvite?.();
      }
    };

    return (
      <Button
        title={isWeb() ? this.state.copyLinkMessage : t('shareInvite')}
        onPress={shareInvite}
        fontColor={Theme.WHITE}
        size={ButtonSize.MEDIUM}
      />
    );
  };

  public isPairedBooking = (): boolean => {
    const { workoutSession } = this.props;

    return 'pairedSessionReference' in workoutSession;
  };

  public isBookingProvisional = (): boolean => {
    const { workoutSession } = this.props;
    return !!workoutSession.pairedSession
      && workoutSession.pairedSession.bookingState === BookingState.ProvisionalBooking;
  };

  public isBookingInvite = (): boolean => {
    const { workoutSession } = this.props;
    return !!workoutSession.pairedSession
      && workoutSession.pairedSession.bookingState === BookingState.BookingInvite;
  };

  public isInviteExpired = (): boolean => {
    const { workoutSession, gymTimezone } = this.props;
    return this.isPairedBooking()
      && !!workoutSession.pairedSession
      && moment.tz(workoutSession.pairedSession.expiresAt, gymTimezone).isBefore(moment().tz(gymTimezone));
  };

  public renderDoneButton = (buttonStyle: ViewStyle = {}) => {
    const { closeModal, t } = this.props;
    return (
      <Button
        fontColor={Theme.WHITE}
        size={ButtonSize.MEDIUM}
        title={t('bookings:done')}
        onPress={closeModal}
        containerStyle={buttonStyle}
      />
    );
  };

  public renderCheckInButton = () => {
    const { t, checkInPressed, checkingInLoading } = this.props;
    return (
      <Button
        fontColor={Theme.WHITE}
        size={ButtonSize.MEDIUM}
        title={t('bookings:checkInSentenceCase')}
        isLoading={checkingInLoading}
        onPress={checkInPressed}
      />
    );
  };

  public renderOperationalButtons = () => {
    const { workoutSession, canStillEditClass } = this.props;

    const isHomeGroupBooking = UserWorkoutSession.isHomeGroupBooking(workoutSession, GymResolver.getByName);
    const showPairedButton = canStillEditClass
      && this.isPairedBooking()
      && !this.isInviteExpired();
    const showSendInviteButton = showPairedButton && this.isBookingProvisional();
    const showChangeInviteButton = showPairedButton && this.isBookingInvite();

    const hasThreeButtons = isHomeGroupBooking || showSendInviteButton || showChangeInviteButton;

    return (
      <View style={styles.container}>
        <View style={[styles.buttonContainer, hasThreeButtons ? styles.thirdWidth : styles.thirtyPercent]}>
          {this.renderCancelBookingButton()}
        </View>

        {isHomeGroupBooking && (
          <View style={[styles.buttonContainer, styles.thirdWidth]}>
            {this.renderShareOpenInviteButton()}
          </View>
        )}

        {showSendInviteButton && (
          <View style={[styles.buttonContainer, styles.thirdWidth]}>
            {this.renderSendInviteButton()}
          </View>
        )}

        {showChangeInviteButton && (
          <View style={[styles.buttonContainer, styles.thirdWidth]}>
            {this.renderChangeInvite()}
          </View>
        )}

        <View style={[styles.buttonContainer, hasThreeButtons ? styles.thirdWidth : styles.seventyPercent]}>
          {this.renderDoneButton()}
        </View>
      </View>
    );
  };

  public render() {
    const { canCheckIn } = this.props;

    if (canCheckIn) {
      return (
        <View style={styles.container}>
          <View style={[styles.buttonContainer, styles.thirdWidth]}>
            {this.renderCancelBookingButton()}
          </View>
          <View style={[styles.buttonContainer, styles.seventyPercent]}>
            {this.renderCheckInButton()}
          </View>
        </View>
      );
    }

    if (this.props.renderCancelOnly) {
      return this.renderCancelBookingButton(styles.thirtyPercent);
    }

    return this.renderOperationalButtons();
  };
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    width: '100%',
  },
  buttonsContainer: {
    flexDirection: 'row',
    marginRight: defaultScale(5),
  },
  buttonContainer: {
    paddingHorizontal: defaultScale(5),
  },
  seventyPercent: {
    flex: 0.7,
  },
  thirtyPercent: {
    flex: 0.3,
    marginRight: defaultScale(5),
  },
  thirdWidth: {
    flex: 0.33,
  },
});

export default BookingOperationButtons;
